import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import { usePlayerApi } from '@/composables/api/usePlayerApi';
import { UrlParameterService, type UrlParameters } from '@/services/url/urlParameterService';
import { SessionStorageService } from '@/services/storage/sessionStorageService';
import { ProductListFiltersStorage } from '@/services/storage/productListFiltersStorage';
import { debounce } from '@/utils/debounce';
import type { MultiRoomAvailabilityCheckResult } from '@/services/api/davinci/checkAvailability';
import { checkMultiRoomAvailability } from '@/services/api/davinci/checkAvailability';
import type { RoomCombination } from '@/services/mappers/offerMapper';
import {
  convertRoomOccupancyToPassengers,
  mapAndGroupOffers,
  enrichRoomCombinationGroupsWithNbc,
} from '@/services/mappers/offerMapper';
import { $t } from '@/composables/translations/useTranslate';
import { getProduct } from '@/services/api/nbc';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { occupancyToPlayerPax } from '@/utils/offers/occupancyToPlayerPax';
import { useAutoExtendWindow } from '@/composables/search/useAutoExtendWindow';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration';
import { maxWeeklyPreset, nightsInRange, type DurationMode } from '@/utils/filters/durationMode';
import { buildDurationPlan } from '@/utils/filters/durationPlan';
import { resolveDurationFlex } from '@/utils/searchDefaults/searchDefaultsResolver';
import { loadIbeSearchDefaults } from '@/composables/filters/useSearchDefaults';
import type { NBCSingleProductResponse } from '@/types/schemas/nbc';
import type { GetProductOffersResult } from '@/types/schemas';

export interface OfferFilterState {
  productCode?: string;
  dateRange?: DateRange;
  roomOccupancy?: RoomOccupancy;
  duration?: number;
  durationMode?: DurationMode;
  anchorStartDate?: string;
}

export const useOfferFiltersStore = defineStore('offerFilters', () => {
  const { productOffers } = usePlayerApi();

  const productCode = ref<string>('');
  const dateRange = ref<DateRange>(null);
  const roomOccupancy = ref<RoomOccupancy>({ rooms: [] });
  const duration = ref<number>(7);
  const durationMode = ref<DurationMode>('preset');
  const durationFlex = computed(() => resolveDurationFlex(loadIbeSearchDefaults()));
  const anchorStartDate = ref<string | null>(null);

  const urlSyncEnabled = ref(true);
  const hasUserInteracted = ref(false);
  const hasTravelDatesInteracted = ref(false);
  const hasPaxInteracted = ref(false);

  // Zero-result date-range auto-extension. On extend, mark user interaction so
  // the new period is persisted/synced.
  const configStore = useFilterConfigurationStore();
  const { autoExtendAttempted, applyAutoExtendedWindow, resetAutoExtend } = useAutoExtendWindow(
    dateRange,
    duration,
    () => maxWeeklyPreset(configStore.durationOptions),
    () => {
      hasUserInteracted.value = true;
    }
  );

  const fetchToken = ref(0);
  const phaseTwoPending = ref(false);

  const multiRoomAvailabilityChecks = ref<Map<string, MultiRoomAvailabilityCheckResult>>(new Map());
  const checkingMultiRoomAvailability = ref<Set<string>>(new Set());

  const nbcData = ref<NBCSingleProductResponse | null>(null);
  const nbcLoading = ref(false);
  const nbcError = ref<string | null>(null);

  const hasDateRange = computed(
    () =>
      dateRange.value !== null &&
      dateRange.value[0] instanceof Date &&
      dateRange.value[1] instanceof Date
  );

  const hasRoomOccupancy = computed(() => roomOccupancy.value.rooms.length > 0);

  const hasDuration = computed(() => duration.value > 0);

  const hasProductCode = computed(() => productCode.value.length > 0);

  const hasAllRequiredParams = computed(
    () => hasDateRange.value && hasDuration.value && hasRoomOccupancy.value && hasProductCode.value
  );

  // The user has explicitly confirmed either travel dates or passengers
  const hasConfirmedSearchParams = computed(
    () => hasTravelDatesInteracted.value || hasPaxInteracted.value
  );

  const missingParams = computed(() => {
    const missing: string[] = [];
    if (!hasDateRange.value) missing.push('date');
    if (!hasDuration.value) missing.push('duration');
    if (!hasRoomOccupancy.value) missing.push('pax');
    if (!hasProductCode.value) missing.push('code');
    return missing;
  });

  // Priority: date → pax → duration → code
  const firstMissingParam = computed(() => {
    if (!hasDateRange.value) return 'date';
    if (!hasRoomOccupancy.value) return 'pax';
    if (!hasDuration.value) return 'duration';
    if (!hasProductCode.value) return 'code';
    return null;
  });
  const apiParams = computed(() => {
    if (!hasDateRange.value || !hasProductCode.value) {
      return null;
    }

    const [startDateObj, endDateObj] = dateRange.value!;
    const startDate = formatDateLocal(startDateObj);
    const endDate = formatDateLocal(endDateObj);

    return {
      startDate,
      endDate,
      duration: duration.value,
      ...occupancyToPlayerPax(roomOccupancy.value),
      productCode: productCode.value,
    };
  });

  const offers = computed(() => productOffers.data.value);
  const loading = computed(() => productOffers.loading.value || phaseTwoPending.value);
  const error = computed(() => productOffers.error.value);
  const errorType = computed(() => {
    const err = productOffers.error.value;
    if (!err) return null;

    const errorStr = typeof err === 'string' ? err : String(err);
    if (errorStr.toLowerCase().includes('timeout')) {
      return 'timeout';
    }
    if (
      errorStr.toLowerCase().includes('network') ||
      errorStr.toLowerCase().includes('connection')
    ) {
      return 'network';
    }
    if (errorStr.toLowerCase().includes('not found') || errorStr.includes('404')) {
      return 'not_found';
    }
    if (
      errorStr.toLowerCase().includes('validation') ||
      errorStr.toLowerCase().includes('invalid')
    ) {
      return 'validation';
    }
    if (errorStr.toLowerCase().includes('soap') || errorStr.toLowerCase().includes('fault')) {
      return 'soap_fault';
    }
    return 'unknown';
  });

  const errorMessage = computed(() => {
    const err = productOffers.error.value;
    if (!err) return '';

    const type = errorType.value;
    if (type === 'timeout') {
      return $t('ibe.error.timeout', 'Request timed out. Please try again.');
    }
    if (type === 'network') {
      return $t('ibe.error.network', 'Unable to connect. Please check your internet connection.');
    }
    if (type === 'not_found') {
      return $t('ibe.error.not-found', 'Product not found. Please check the product code.');
    }
    if (type === 'validation') {
      return $t(
        'ibe.error.validation',
        'Invalid search parameters. Please adjust your search criteria.'
      );
    }
    if (type === 'soap_fault') {
      return $t('ibe.error.soap-fault', 'API error occurred. Please try again later.');
    }
    return typeof err === 'string' ? err : $t('ibe.error.unknown', 'An unexpected error occurred.');
  });

  const passengerDetails = computed(() => {
    if (!roomOccupancy.value || roomOccupancy.value.rooms.length === 0) {
      return [];
    }
    return convertRoomOccupancyToPassengers(roomOccupancy.value);
  });

  const roomCombinationGroups = computed(() => {
    if (!offers.value || !roomOccupancy.value || roomOccupancy.value.rooms.length === 0) {
      return [];
    }

    const groups = mapAndGroupOffers(offers.value, roomOccupancy.value, {
      selectedDuration: duration.value,
      anchorStartDate: anchorStartDate.value ?? undefined,
    });

    return enrichRoomCombinationGroupsWithNbc(groups, nbcData.value);
  });

  const isMultiRoom = computed(() => {
    return roomCombinationGroups.value.some((group) => group.isMultiRoom);
  });

  /**
   * Forget which offer the user arrived from.
   *
   * Called by the setters rather than by watching `hasUserInteracted`, because
   * the zero-result window auto-extension also flags an interaction — and that
   * is precisely the case where the deep link's offer still needs to rank first.
   */
  function dropAnchor(): void {
    anchorStartDate.value = null;
  }

  function setProductCode(code: string): void {
    if (productCode.value === code) return;
    hasUserInteracted.value = true;
    dropAnchor();
    resetAutoExtend();
    productCode.value = code;
  }

  function setDateRange(range: DateRange): void {
    // Applying the travel-period modal is an interaction even when the user
    // confirms the URL/default values unchanged.
    if (range?.[0] instanceof Date && range[1] instanceof Date) {
      hasTravelDatesInteracted.value = true;
    }
    hasUserInteracted.value = true;

    const newRangeString = JSON.stringify(range?.map((d) => (d ? d.getTime() : null)));
    const currentRangeString = JSON.stringify(
      dateRange.value?.map((d) => (d ? d.getTime() : null))
    );
    if (newRangeString === currentRangeString) return;

    dropAnchor();
    resetAutoExtend();
    dateRange.value = range;
  }

  function setRoomOccupancy(occupancy: RoomOccupancy): void {
    if (occupancy.rooms.length > 0) {
      hasPaxInteracted.value = true;
    }

    const newOccupancyString = JSON.stringify(occupancy);
    const currentOccupancyString = JSON.stringify(roomOccupancy.value);
    if (newOccupancyString === currentOccupancyString) return;

    hasUserInteracted.value = true;
    dropAnchor();
    resetAutoExtend();
    roomOccupancy.value = occupancy;
  }

  function setDuration(days: number): void {
    if (duration.value === days) return;
    hasUserInteracted.value = true;
    dropAnchor();
    resetAutoExtend();
    duration.value = days;
  }

  function setDurationMode(mode: DurationMode): void {
    if (durationMode.value === mode) return;
    hasUserInteracted.value = true;
    dropAnchor();
    resetAutoExtend();
    durationMode.value = mode;
  }

  function widenedDurations(): number[] | null {
    const plan = buildDurationPlan(
      duration.value,
      nightsInRange(dateRange.value),
      durationFlex.value
    );
    return plan.durations.length > 1 ? plan.durations : null;
  }

  function countOffers(result: GetProductOffersResult | null): number {
    return result?.Products?.Product?.length ?? 0;
  }

  /**
   * Fetch offers for the current parameters, in up to two phases.
   *
   * Phase one asks for the selected length alone, which is what the page has
   * always done. Only when that succeeds and still finds nothing does phase two
   * repeat the request with a range of lengths around it — a product that exists
   * only in 5 or 6 nights is invisible to phase one, and this is the fallback that
   * finds it. Widening on a failed request would double the latency of every error
   * and re-fire a query the user has already left, so the retry is gated on
   * `Success` rather than on emptiness alone. An explicit Exact intent skips it.
   *
   * The date-window auto-extension stays the last resort, after both phases.
   */
  async function fetchOffers(): Promise<void> {
    const params = apiParams.value;
    if (!params) {
      console.warn('[offerFilters] Cannot fetch offers: required params missing');
      return;
    }

    multiRoomAvailabilityChecks.value.clear();
    checkingMultiRoomAvailability.value.clear();

    const token = ++fetchToken.value;
    let result = await productOffers.call(params);

    const shouldWiden =
      result?.Success &&
      countOffers(result) === 0 &&
      durationMode.value !== 'exact' &&
      token === fetchToken.value;

    const widened = shouldWiden ? widenedDurations() : null;

    if (widened) {
      phaseTwoPending.value = true;
      try {
        result = await productOffers.call({ ...params, durations: widened });
      } catch (error) {
        console.warn('[offerFilters] Widened retry failed, keeping the first result', error);
      } finally {
        phaseTwoPending.value = false;
      }
    }

    if (token !== fetchToken.value) {
      return;
    }

    if (
      result?.Success &&
      countOffers(result) === 0 &&
      !autoExtendAttempted.value &&
      hasDateRange.value
    ) {
      applyAutoExtendedWindow();
    }
  }

  async function retryFetch(): Promise<void> {
    await fetchOffers();
  }

  async function fetchNbcData(): Promise<void> {
    if (!productCode.value) {
      console.warn('[NBC] Cannot fetch - no product code');
      return;
    }

    nbcLoading.value = true;
    nbcError.value = null;

    try {
      nbcData.value = await getProduct(productCode.value);
    } catch (error) {
      console.warn('[NBC] Data unavailable for product:', productCode.value, error);
      nbcError.value = error instanceof Error ? error.message : 'Unknown error';
      nbcData.value = null;
    } finally {
      nbcLoading.value = false;
    }
  }

  function cancelFetch(): void {
    productOffers.cancel();
  }

  function setInitialState(state: OfferFilterState): void {
    const wasUrlSyncEnabled = urlSyncEnabled.value;
    urlSyncEnabled.value = false;

    try {
      if (state.productCode !== undefined) {
        productCode.value = state.productCode;
      }
      if (state.dateRange !== undefined) {
        dateRange.value = state.dateRange;
      }
      if (state.roomOccupancy !== undefined) {
        roomOccupancy.value = state.roomOccupancy;
      }
      if (state.duration !== undefined) {
        duration.value = state.duration;
      }
      if (state.durationMode !== undefined) {
        durationMode.value = state.durationMode;
      }
      if (state.anchorStartDate !== undefined) {
        anchorStartDate.value = state.anchorStartDate;
      }
    } finally {
      urlSyncEnabled.value = wasUrlSyncEnabled;
    }
  }

  // URL synchronization (debounced)
  const syncToUrlDebounced = debounce(() => {
    if (!urlSyncEnabled.value || !hasUserInteracted.value) return;

    const params: UrlParameters = {
      startDate: dateRange.value?.[0] || undefined,
      endDate: dateRange.value?.[1] || undefined,
      duration: duration.value,
      durationMode: durationMode.value,
      roomOccupancy: roomOccupancy.value,
    };

    UrlParameterService.updateUrlParameters(params);
  }, 300);

  watch(
    [productCode, dateRange, duration, durationMode, roomOccupancy],
    () => {
      syncToUrlDebounced();
    },
    { deep: true }
  );

  watch(
    [dateRange, duration, durationMode, roomOccupancy],
    () => {
      if (!urlSyncEnabled.value || !hasUserInteracted.value) return;
      SessionStorageService.write({
        dateRange: dateRange.value,
        roomOccupancy: roomOccupancy.value,
        duration: duration.value,
        durationMode: durationMode.value,
      });
    },
    { deep: true }
  );

  watch(
    [dateRange, duration, roomOccupancy],
    () => {
      if (!hasUserInteracted.value) return;
      ProductListFiltersStorage.merge({
        dateRange: hasDateRange.value ? dateRange.value : undefined,
        roomOccupancy: hasRoomOccupancy.value ? roomOccupancy.value : undefined,
        duration: hasDuration.value ? duration.value : undefined,
      });
    },
    { deep: true }
  );

  function reset(): void {
    productCode.value = '';
    dateRange.value = null;
    roomOccupancy.value = { rooms: [] };
    duration.value = 7;
    durationMode.value = 'preset';
    anchorStartDate.value = null;
    hasUserInteracted.value = false;
    hasTravelDatesInteracted.value = false;
    hasPaxInteracted.value = false;
    resetAutoExtend();
  }

  /**
   * Check availability for a complete room combination
   *
   * Checks availability of multiple rooms booked together with unified passenger list.
   * Creates multi-service DaVinci request with proper passenger assignment per room.
   *
   * @param combination - RoomCombination to check (multiple products/rooms)
   * @param productCodeOverride - Optional product code override (defaults to store's productCode)
   */
  async function checkCombinationAvailability(
    combination: RoomCombination,
    productCodeOverride?: string
  ): Promise<void> {
    const combinationId = combination.products.map((p) => p.offerId).join('-');

    if (checkingMultiRoomAvailability.value.has(combinationId)) {
      console.warn(`Already checking availability for combination ${combinationId}`);
      return;
    }

    checkingMultiRoomAvailability.value.add(combinationId);

    const result = await checkMultiRoomAvailability({
      combination,
      productCode: productCodeOverride || productCode.value,
      // Consistency with checkout's first BA check with blank TravelType
      travelType: '',
    });

    multiRoomAvailabilityChecks.value.set(combinationId, result);
    checkingMultiRoomAvailability.value.delete(combinationId);
  }

  function isCheckingCombinationAvailability(combinationId: string): boolean {
    return checkingMultiRoomAvailability.value.has(combinationId);
  }

  function getCombinationAvailabilityResult(
    combinationId: string
  ): MultiRoomAvailabilityCheckResult | undefined {
    return multiRoomAvailabilityChecks.value.get(combinationId);
  }

  /**
   * Generate unique combination ID from RoomCombination
   * Used to identify combinations for availability tracking
   */
  function generateCombinationId(combination: RoomCombination): string {
    return combination.products.map((p) => p.offerId).join('-');
  }

  return {
    // State
    productCode,
    dateRange,
    roomOccupancy,
    duration,
    durationMode,
    anchorStartDate,
    urlSyncEnabled,
    hasUserInteracted,
    hasTravelDatesInteracted,
    hasPaxInteracted,

    // Computed - Validation
    hasDateRange,
    hasRoomOccupancy,
    hasDuration,
    hasProductCode,
    hasAllRequiredParams,
    hasConfirmedSearchParams,
    missingParams,
    firstMissingParam,
    apiParams,

    // Computed - API
    offers,
    loading,
    error,
    errorType,
    errorMessage,

    // Computed - Phase 2 Multi-room Support
    passengerDetails,
    roomCombinationGroups,
    isMultiRoom,

    // Computed - Phase 3 Multi-Room Availability
    multiRoomAvailabilityChecks: computed(() => multiRoomAvailabilityChecks.value),
    checkingMultiRoomAvailability: computed(() => checkingMultiRoomAvailability.value),

    // Computed - NBC
    nbcData: computed(() => nbcData.value),
    nbcLoading: computed(() => nbcLoading.value),
    nbcError: computed(() => nbcError.value),

    // Actions
    setProductCode,
    setDateRange,
    setRoomOccupancy,
    setDuration,
    setDurationMode,
    fetchOffers,
    retryFetch,
    cancelFetch,
    fetchNbcData,
    setInitialState,
    reset,
    checkCombinationAvailability,
    isCheckingCombinationAvailability,
    getCombinationAvailabilityResult,
    generateCombinationId,
  };
});
